import React from 'react';

import Button from 'components/Button/Button';
import { Text, Select, useBreakpointValue } from '@chakra-ui/react';

import colors from 'theme/Colors.module.scss';
import './Pagination.scss';

const Pagination = props => {
  const {
    data = [],
    table,
    pagination,
    setPagination,
    setSelectedRowId,
  } = props;

  const currentPage = table.getState().pagination.pageIndex + 1;
  const totalPages = data?.total_pages || 1;
  const maxVisible = useBreakpointValue({
    base: 3,
    sm: 3,
    md: 5,
    lg: 7,
    xl: 9,
  }); // minimum 3 for pagination to work properly

  const renderPages = () => {
    const half = Math.floor(maxVisible / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, currentPage + half);

    if (currentPage <= half) {
      end = Math.min(totalPages, maxVisible);
    }

    if (currentPage + half >= totalPages) {
      start = Math.max(1, totalPages - maxVisible + 1);
    }

    const pages = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages.length > 0 ? pages : [0];
  };

  // Handle page change
  const handlePageChange = newPage => {
    setPagination({ ...pagination, pageIndex: newPage });
    setSelectedRowId(null);
  };
  return (
    <>
      <ul className="pagination">
        {/* Page size selector */}
        <li className="size-select" key="size-select">
          <Select
            size="sm"
            width="auto"
            value={pagination.pageSize}
            onChange={e =>
              setPagination(prev => ({
                ...prev,
                pageSize: Number(e.target.value),
                pageIndex: 0, // reset to first page when size changes
              }))
            }
          >
            {[10, 20, 50].map(size => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </Select>
        </li>

        {/* Prev button */}
        <li key="prev-btn" className="prev-btn">
          <Button
            icon="back"
            iconColor={colors.slate}
            btnStyle="transparent"
            iconBaseline="false"
            disabled={table.getState().pagination.pageIndex < 1}
            onClick={() => handlePageChange(pagination.pageIndex - 1)}
          />
        </li>

        {/* First Page Numbers */}
        {renderPages().length > 0 &&
          !renderPages().includes(0) &&
          !renderPages().includes(1) && (
            <li className="prefix-btn-start" key="prefix-btn-start">
              <Button
                key={1}
                text={1}
                onClick={() => handlePageChange(0)}
                textColor={1 === currentPage ? colors.white : colors.slate}
                bgColor={1 === currentPage ? colors.slate : colors.lighterGrey}
                btnStyle={1 === currentPage ? 'solid' : 'ghost'}
              >
                {1}
              </Button>
            </li>
          )}

        {/* Left Ellipsis */}
        {renderPages().length > 0 &&
          !renderPages().includes(0) &&
          !renderPages().includes(1) && (
            <li className="left-ellipsis" key="left-ellipsis">
              <i className="fa-solid fa-ellipsis"></i>
            </li>
          )}

        {/* Page Numbers */}
        {renderPages().map(page => {
          if (page !== 0) {
            return (
              <li className="page-number" key={page}>
                <Button
                  key={page}
                  text={page}
                  onClick={() => handlePageChange(page - 1)}
                  textColor={page === currentPage ? colors.white : colors.slate}
                  bgColor={
                    page === currentPage ? colors.slate : colors.lighterGrey
                  }
                  btnStyle={page === currentPage ? 'solid' : 'ghost'}
                ></Button>
              </li>
            );
          } else {
            return (
              <li className="page-number" key={1}>
                <Button
                  key={1}
                  text={1}
                  textColor={colors.white}
                  bgColor={colors.slate}
                  btnStyle={'solid'}
                ></Button>
              </li>
            );
          }
        })}

        {/* Right Ellipsis */}
        {renderPages().length > 0 &&
          !renderPages().includes(0) &&
          !renderPages().includes(totalPages) && (
            <li className="right-ellipsis" key="right-ellipsis">
              <i className="fa-solid fa-ellipsis"></i>
            </li>
          )}

        {/* Last Page Numbers */}
        {renderPages().length > 0 && !renderPages().includes(totalPages) && (
          <li className="prefix-btn-end" key="prefix-btn-end">
            <Button
              key={totalPages}
              text={totalPages}
              onClick={() => handlePageChange(totalPages - 1)}
              textColor={
                totalPages === currentPage ? colors.white : colors.slate
              }
              bgColor={
                totalPages === currentPage ? colors.slate : colors.lighterGrey
              }
              btnStyle={totalPages === currentPage ? 'solid' : 'ghost'}
            >
              {totalPages}
            </Button>
          </li>
        )}

        {/* Next button */}
        <li key="next-btn" className="next-btn">
          <Button
            icon="next"
            iconColor={colors.slate}
            btnStyle="transparent"
            iconBaseline="false"
            disabled={table.getState().pagination.pageIndex === totalPages - 1}
            onClick={() => handlePageChange(pagination.pageIndex + 1)}
          />
        </li>

        {/* Page text */}
        <li className="page-info" key="page-info">
          <Text as="span" display="flex" alignItems="center">
            Showing &nbsp;
            <Text as="span" fontWeight="bold">
              {table.getState().pagination.pageIndex + 1}
            </Text>
            &nbsp; of &nbsp;
            <Text as="span" fontWeight="bold">
              {totalPages || 1}
            </Text>
          </Text>
        </li>
      </ul>
    </>
  );
};

export default Pagination;
